import { LocalizationProvider, StaticDatePicker, StaticDateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { IconButton, InputAdornment, Popover, TextField } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { IBuildlingBlockInputControl } from "./BuildingBlockInterfaces";

export interface UIDatePickerProps extends IBuildlingBlockInputControl<Date> {
    showTime?: boolean;
}

export function UIDatePicker(props: UIDatePickerProps) {
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            {props.showTime ? (
                <StyledDatePicker
                    showTime={true}
                    error={props.error}
                    label={props.label}
                    hint={props.hint}
                    value={props.value}
                    onChange={props.onChange}
                    fullWidth={props.fullWidth}
                    size={props.size ?? "small"}
                    variant={props.variant}
                    sx={props.sx}
                ></StyledDatePicker>
            ) : (
                <StyledDatePicker
                    label={props.label}
                    error={props.error}
                    hint={props.hint}
                    value={props.value}
                    onChange={props.onChange}
                    fullWidth={props.fullWidth}
                    size={props.size ?? "small"}
                    variant={props.variant}
                    sx={props.sx}
                ></StyledDatePicker>
            )}
        </LocalizationProvider>
    );
}

export interface StyledDatePickerProps extends IBuildlingBlockInputControl<Date> {
    showTime?: boolean;
}

export function StyledDatePicker(props: StyledDatePickerProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const [err, setErr] = useState<string | undefined>(props.error);

    const [val, setVal] = useState<Date | undefined>();
    const [sval, setSVal] = useState<string | undefined>();

    useEffect(() => {
        setVal(props.value);
        setSVal(getFormattedDate(props.value));
    }, [props.value]);

    const handleBlur = () => {
        const dateString = sval;
        const isValid = isValidDate(dateString);

        if (isValid) {
            if (props.showTime) {
                setErr(props.error);

                const parsedDate = new Date(dateString!);

                setVal(parsedDate);
                setSVal(getFormattedDate(parsedDate));
                props.onChange(parsedDate);
            } else {
                setErr(props.error);
                const [year, month, day] = dateString!.split("-");
                const parsedDate = new Date(`${month}/${day}/${year}`);
                setVal(parsedDate);
                setSVal(getFormattedDate(parsedDate));
                props.onChange(parsedDate);
            }
        } else {
            setErr("Date parsing error");
        }
    };
    const regex = /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/;
    const dateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
    const isValidDate = (date: String | undefined) => {
        if (date === undefined || date === "") return false;
        if (props.showTime) {
            if (!date.match(dateTimeRegex)) return false;
            return true;
        } else {
            if (!date.match(regex)) return false;

            const [year, month, day] = date.split("/");
            const parsedDate = new Date(`${month}/${day}/${year}`);
            if (isNaN(parsedDate.getTime())) return false;

            return true;
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    function getFormattedDate(v: Date | undefined): string {
        if (!v) return "";

        if (props.showTime) {
            const d = v;
            const month = d.getMonth() + 1;
            const day = d.getDate();
            const year = d.getFullYear();
            const hour = d.getHours();
            const minute = d.getMinutes();
            return `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}T${hour
                .toString()
                .padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
        } else {
            const day = v!.getDate();
            const month = v!.getMonth() + 1;
            const year = v!.getFullYear();

            return `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
        }
    }

    function getFormatDateMessage(): string {
        if (props.showTime) return "Enter date in format dd/mm/yyyy HH:MM (AM/PM Optional)";
        else return "Enter date in format dd/mm/yyyy";
    }

    return (
        <>
            <TextField
                label={props.label}
                value={sval ?? ""}
                onChange={(e) => {
                    setSVal(e.target.value);
                }}
                onBlur={() => handleBlur()}
                error={Boolean(err)}
                size={props.size}
                variant={props.variant}
                type={props.showTime ? "datetime-local" : "date"}
                onClick={(e) => e.preventDefault()}
                fullWidth={props.fullWidth}
                helperText={err ?? props.hint}
                sx={{
                    '& input[type="date"]::-webkit-calendar-picker-indicator': {
                        display: "none",
                        WebkitAppearance: "none",
                        filter: "invert(78%) sepia(66%) saturate(6558%) hue-rotate(84deg) brightness(127%) contrast(116%)",
                    },
                    '& input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
                        display: "none",
                        WebkitAppearance: "none",
                        filter: "invert(78%) sepia(66%) saturate(6558%) hue-rotate(84deg) brightness(127%) contrast(116%)",
                    },
                    ...props.sx,
                }}
                // sx={{
                //
                //     "-webkit-calendar-picker-indicator": {
                //     display: "none";
                //     "-webkit-appearance": "none";
                // }
                // }}
                InputLabelProps={{
                    shrink: true,
                }}
                inputProps={{
                    //pattern: props.showTime ? dateTimeRegex : regex,
                    title: getFormatDateMessage(),
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
                                <CalendarMonthIcon />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
            >
                {props.showTime && (
                    <StaticDateTimePicker
                        orientation="landscape"
                        value={dayjs(val)}
                        onChange={(value) => {
                            // @ts-ignore
                            setVal(value.toDate());
                            // @ts-ignore
                            setSVal(getFormattedDate(value.toDate()));
                            setErr(props.error);
                        }}
                        onAccept={() => {
                            setAnchorEl(null);
                            if (val) props.onChange(val);
                        }}
                    />
                )}
                {!props.showTime && (
                    <StaticDatePicker
                        orientation="landscape"
                        value={dayjs(val)}
                        onChange={(value) => {
                            // @ts-ignore
                            setVal(value.toDate());
                            // @ts-ignore
                            setSVal(getFormattedDate(value.toDate()));
                            // @ts-ignore
                            setErr(props.error);
                        }}
                        onAccept={() => {
                            setAnchorEl(null);
                            if (val) props.onChange(val);
                        }}
                    />
                )}
            </Popover>
        </>
    );
}
